import React, { useEffect, useState } from "react";
import { Eye, Globe, Loader2, TrendingUp, Users } from "lucide-react";
import { api } from "../../lib/api";

const REF_LABEL = (s) => {
  if (!s) return "Direct";
  const r = s.toLowerCase();
  if (r === "direct") return "Direct (geen referrer)";
  if (r.includes("google")) return "Google";
  if (r.includes("bing")) return "Bing";
  if (r.includes("facebook")) return "Facebook";
  if (r.includes("instagram")) return "Instagram";
  if (r.includes("linkedin")) return "LinkedIn";
  if (r.includes("tiktok")) return "TikTok";
  if (r.includes("youtube")) return "YouTube";
  if (r.includes("twitter") || r.includes("x.com") || r.includes("t.co")) return "Twitter/X";
  if (r.includes("intern")) return "Interne links (woningalert)";
  return s;
};

/**
 * Admin-only. Compact "waar komt mijn traffic vandaan"-paneel per koopwoning.
 * Props: lid (string). Rendert niets voor non-admin op API-fout.
 */
export const ListingTrafficPanel = ({ lid, defaultOpen = false, days = 30 }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open || data || err) return;
    api(`/admin/koop/${lid}/traffic?days=${days}`)
      .then(setData)
      .catch(() => setErr(true));
  }, [open, data, err, lid, days]);

  const maxDaily = Math.max(1, ...((data?.daily || []).map((d) => d.count)));

  return (
    <div className="rounded-xl border border-border bg-white text-xs" data-testid="listing-traffic-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left font-semibold text-navy hover:bg-slate-50"
        data-testid="listing-traffic-toggle"
      >
        <span className="flex items-center gap-2"><TrendingUp size={13} className="text-brandblue" /> Waar komt deze traffic vandaan?</span>
        <span className="text-slate-400">{open ? "Verberg" : "Bekijk"} ↓</span>
      </button>

      {open && (
        <div className="border-t border-border px-3 py-3">
          {!data && !err && (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 size={12} className="animate-spin" /> Laden…</div>
          )}
          {err && <div className="text-slate-500">Geen data beschikbaar.</div>}

          {data && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-slate-50 p-2">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400"><Eye size={11} /> Pageviews</div>
                  <div className="mt-0.5 text-base font-extrabold text-navy tabular-nums">{data.total_pageviews}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-2">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400"><Users size={11} /> Unieke bezoekers</div>
                  <div className="mt-0.5 text-base font-extrabold text-navy tabular-nums">{data.unique_sessions}</div>
                </div>
              </div>

              {data.daily.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Laatste {data.days} dagen</div>
                  <div className="mt-1.5 flex h-10 items-end gap-[2px]" data-testid="traffic-sparkline">
                    {data.daily.map((d) => (
                      <div
                        key={d.date}
                        title={`${d.date}: ${d.count}`}
                        className="flex-1 rounded-sm bg-brandblue/70"
                        style={{ height: `${Math.max(6, (d.count / maxDaily) * 100)}%` }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><Globe size={11} /> Bronnen</div>
                {data.top_referrers.length === 0 ? (
                  <div className="mt-1 text-slate-500">Nog geen bezoekers.</div>
                ) : (
                  <ul className="mt-1 space-y-0.5" data-testid="traffic-top-refs">
                    {data.top_referrers.map((r) => {
                      const pct = Math.round((r.count / data.total_pageviews) * 100);
                      return (
                        <li key={r.source} className="flex items-center justify-between gap-2 rounded-md px-1.5 py-1 hover:bg-slate-50">
                          <span className="truncate text-slate-700">{REF_LABEL(r.source)}</span>
                          <span className="shrink-0 tabular-nums font-semibold text-navy">{r.count} <span className="text-slate-400">· {pct}%</span></span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {data.top_utm.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Campagnes (UTM)</div>
                  <ul className="mt-1 space-y-0.5" data-testid="traffic-top-utm">
                    {data.top_utm.map((u, i) => (
                      <li key={i} className="rounded-md bg-slate-50 px-2 py-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-navy">{u.source || "—"}</span>
                          <span className="tabular-nums text-slate-500">{u.count}</span>
                        </div>
                        <div className="truncate text-slate-500">{u.medium || "—"} · {u.campaign || "—"}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingTrafficPanel;
